/**
 * EXPERIENCE SWITCHER: THE LEARNER'S STORED CHOICE, PER TAB.
 *
 * A learner in two or more delivery contexts picks one. The choice is kept in
 * sessionStorage under a key scoped to the signed-in user, so it survives the
 * page reload a switch performs and never carries to another account. A new
 * tab starts empty, but a DUPLICATED tab inherits the session storage of the
 * tab it was copied from, so a stored choice is a claim to revalidate against
 * the discovered list, never an authority (the server re-checks ownership on
 * every mission read regardless).
 *
 * Every access is wrapped: a browser that blocks storage reads as "no stored
 * choice" and a failed write reports false, so the caller can skip the reload.
 */

const CHOICE_KEY_PREFIX = "experienceSwitcher.choice.";

/* The M2 reload record (ExperienceContext.jsx owns the guard). A deliberate
   switch writes it too, so an automatic context change in the 30 seconds after
   a switch is still held instead of reloading again. */
export const RELOAD_KEY = "m2.contextReload";

const positiveId = (value) => {
  const n = Number(value);
  return value !== null &&
    value !== undefined &&
    Number.isSafeInteger(n) &&
    n > 0
    ? n
    : null;
};

const keyFor = (userId) => {
  const id = positiveId(userId);
  return id === null ? null : `${CHOICE_KEY_PREFIX}${id}`;
};

/** `{ squadId, cohortId }` for this user in this tab, or null. Never throws. */
export const readContextChoice = (userId) => {
  const key = keyFor(userId);
  if (key === null) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const squadId = positiveId(parsed?.squadId);
    const cohortId = positiveId(parsed?.cohortId);
    return squadId !== null && cohortId !== null ? { squadId, cohortId } : null;
  } catch (_error) {
    return null;
  }
};

/** Stores the choice. True only when the write itself succeeded. */
export const writeContextChoice = (userId, { squadId, cohortId }) => {
  const key = keyFor(userId);
  if (key === null) return false;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ squadId, cohortId }));
    return true;
  } catch (_error) {
    return false;
  }
};

/** Forgets a stored choice that no longer names one of the contexts. */
export const clearContextChoice = (userId) => {
  const key = keyFor(userId);
  if (key === null) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch (_error) {
    // Nothing to forget in a browser that blocks storage.
  }
};

/**
 * Records the deliberate switch's reload in the M2 reload record. It does not
 * READ the record: a click is never held by the 30-second guard. Best effort.
 */
export const recordDeliberateReload = ({ userId, from, to }) => {
  try {
    window.sessionStorage.setItem(
      RELOAD_KEY,
      JSON.stringify({ userId, from, to, at: Date.now(), deliberate: true }),
    );
  } catch (_error) {
    // The choice itself was stored; the reload still happens.
  }
};
